/**
 * shared/math.ts — vectors, angles, deterministic trig, float32 helpers, coordinate conversion.
 *
 * OWNER: integration (contract). Change requests: docs/CONTRACT-REQUESTS.md.
 *
 * ============================================================================================
 *  COORDINATE CONVENTION  (read this before touching positions anywhere)
 * ============================================================================================
 *  Everything in shared/ and sim/ (pmove, collision, weapons, missiles, antilag, bots, protocol)
 *  uses the ENGINE's space, exactly like the original C code:
 *      CoD space: right-handed, Z-UP, X forward/east, Y left/north, units = inches.
 *      Angles: [PITCH, YAW, ROLL] in degrees. yaw 0 = +X, yaw 90 = +Y. Positive pitch looks DOWN.
 *  three.js (client/render, client/viewmodel, client/camera) is Y-UP. Convert ONLY at the
 *  render boundary with the helpers at the bottom of this file:
 *      three = (cod.x, cod.z, -cod.y)          cod = (three.x, -three.z, three.y)
 *  (Same rotation as OAT's glTF writer and tools/map; see tools/map/README.md "Conventions".)
 *  Asset files that are ALREADY in three space: map.glb, collision.bin positions (!),
 *  spawns.json `origin` (use `originCod` instead), meta.json `mapCenter` (use spawns `mapCenterCod`).
 *  The collision loader converts collision.bin to CoD space once at load time.
 * ============================================================================================
 *
 *  DETERMINISM [NET §12 pitfall 2]: Math.sin/cos/atan2/pow differ between V8 and JavaScriptCore.
 *  Anything that feeds pmove, weapons or missiles MUST use dsin/dcos/datan2/dasin/dacos below
 *  (built only from + - * / sqrt floor round, which are IEEE-exact everywhere). Math.sqrt is fine.
 *  Run fround over float state at the end of every pmove step (PlayerState is f32 on the wire).
 */

export type Vec3 = [number, number, number];
/** Read-only view of a vector (constants, inputs that must not be mutated). */
export type ReadonlyVec3 = readonly [number, number, number];

export const PITCH = 0;
export const YAW = 1;
export const ROLL = 2;

export const PI = 3.141592653589793;
export const TWO_PI = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
export const DEG2RAD = PI / 180;
export const RAD2DEG = 180 / PI;

// ---------------------------------------------------------------------------------------------
// Vec3 (out-parameter style: no allocation in hot loops)
// ---------------------------------------------------------------------------------------------
export function vec3(x = 0, y = 0, z = 0): Vec3 {
  return [x, y, z];
}
export function vcopy(out: Vec3, a: ReadonlyVec3): Vec3 {
  out[0] = a[0]; out[1] = a[1]; out[2] = a[2];
  return out;
}
export function vset(out: Vec3, x: number, y: number, z: number): Vec3 {
  out[0] = x; out[1] = y; out[2] = z;
  return out;
}
export function vclone(a: ReadonlyVec3): Vec3 {
  return [a[0], a[1], a[2]];
}
export function vadd(out: Vec3, a: ReadonlyVec3, b: ReadonlyVec3): Vec3 {
  out[0] = a[0] + b[0]; out[1] = a[1] + b[1]; out[2] = a[2] + b[2];
  return out;
}
export function vsub(out: Vec3, a: ReadonlyVec3, b: ReadonlyVec3): Vec3 {
  out[0] = a[0] - b[0]; out[1] = a[1] - b[1]; out[2] = a[2] - b[2];
  return out;
}
export function vscale(out: Vec3, a: ReadonlyVec3, s: number): Vec3 {
  out[0] = a[0] * s; out[1] = a[1] * s; out[2] = a[2] * s;
  return out;
}
/** Q3 VectorMA: out = a + b*s */
export function vma(out: Vec3, a: ReadonlyVec3, s: number, b: ReadonlyVec3): Vec3 {
  out[0] = a[0] + b[0] * s; out[1] = a[1] + b[1] * s; out[2] = a[2] + b[2] * s;
  return out;
}
export function vdot(a: ReadonlyVec3, b: ReadonlyVec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
export function vcross(out: Vec3, a: ReadonlyVec3, b: ReadonlyVec3): Vec3 {
  const x = a[1] * b[2] - a[2] * b[1];
  const y = a[2] * b[0] - a[0] * b[2];
  const z = a[0] * b[1] - a[1] * b[0];
  out[0] = x; out[1] = y; out[2] = z;
  return out;
}
export function vlengthSq(a: ReadonlyVec3): number {
  return a[0] * a[0] + a[1] * a[1] + a[2] * a[2];
}
export function vlength(a: ReadonlyVec3): number {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
}
export function vlength2D(a: ReadonlyVec3): number {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1]);
}
export function vdistance(a: ReadonlyVec3, b: ReadonlyVec3): number {
  const x = a[0] - b[0], y = a[1] - b[1], z = a[2] - b[2];
  return Math.sqrt(x * x + y * y + z * z);
}
export function vdistanceSq(a: ReadonlyVec3, b: ReadonlyVec3): number {
  const x = a[0] - b[0], y = a[1] - b[1], z = a[2] - b[2];
  return x * x + y * y + z * z;
}
/** Q3 VectorNormalize: normalizes in place (into out), returns the original length. Zero stays zero. */
export function vnormalize(out: Vec3, a: ReadonlyVec3 = out): number {
  const len = Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
  if (len) {
    const il = 1 / len;
    out[0] = a[0] * il; out[1] = a[1] * il; out[2] = a[2] * il;
  } else {
    out[0] = 0; out[1] = 0; out[2] = 0;
  }
  return len;
}
export function vlerp(out: Vec3, a: ReadonlyVec3, b: ReadonlyVec3, t: number): Vec3 {
  out[0] = a[0] + (b[0] - a[0]) * t;
  out[1] = a[1] + (b[1] - a[1]) * t;
  out[2] = a[2] + (b[2] - a[2]) * t;
  return out;
}
export function vequals(a: ReadonlyVec3, b: ReadonlyVec3): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
export function vclear(out: Vec3): Vec3 {
  out[0] = 0; out[1] = 0; out[2] = 0;
  return out;
}

// ---------------------------------------------------------------------------------------------
// float32 helpers (PlayerState floats are f32 on the wire; sim keeps them at f32 precision)
// ---------------------------------------------------------------------------------------------
export const f32 = Math.fround;
export function fround3(v: Vec3): Vec3 {
  v[0] = Math.fround(v[0]); v[1] = Math.fround(v[1]); v[2] = Math.fround(v[2]);
  return v;
}
/** BO2 SnapVector (0x502120): TRUNCATE toward zero (cvttss2si), not round like Q3 [PM §2]. */
export function snapVectorTrunc(v: Vec3): Vec3 {
  v[0] = Math.trunc(v[0]) + 0; v[1] = Math.trunc(v[1]) + 0; v[2] = Math.trunc(v[2]) + 0; // +0 kills -0
  return v;
}
export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ---------------------------------------------------------------------------------------------
// Deterministic trig (radians). Max abs error ~1e-12; identical bits on every JS engine.
// ---------------------------------------------------------------------------------------------
const INV_TWO_PI = 1 / TWO_PI;

/** Deterministic sine (radians). */
export function dsin(x: number): number {
  // reduce to [-pi, pi]
  let r = x - TWO_PI * Math.round(x * INV_TWO_PI);
  // fold to [-pi/2, pi/2]
  if (r > HALF_PI) r = PI - r;
  else if (r < -HALF_PI) r = -PI - r;
  const r2 = r * r;
  // Taylor to x^17 (term ratio at pi/2 < 1e-11)
  return r * (1 + r2 * (-1 / 6 + r2 * (1 / 120 + r2 * (-1 / 5040 + r2 * (1 / 362880 + r2 * (-1 / 39916800 + r2 * (1 / 6227020800 + r2 * (-1 / 1307674368000 + r2 * (1 / 355687428096000)))))))));
}
/** Deterministic cosine (radians). */
export function dcos(x: number): number {
  return dsin(x + HALF_PI);
}
/** atan for |t| <= ~0.2 (series). */
function atanSmall(t: number): number {
  const t2 = t * t;
  return t * (1 + t2 * (-1 / 3 + t2 * (1 / 5 + t2 * (-1 / 7 + t2 * (1 / 9 + t2 * (-1 / 11 + t2 * (1 / 13 + t2 * (-1 / 15 + t2 * (1 / 17 + t2 * (-1 / 19 + t2 * (1 / 21 + t2 * (-1 / 23))))))))))));
}
/** Deterministic atan (radians). */
export function datan(x: number): number {
  if (x !== x) return NaN;
  const neg = x < 0;
  let a = neg ? -x : x;
  let base = 0;
  let invert = false;
  if (a > 1) { a = 1 / a; invert = true; }
  if (a > 0.41421356237309503) { // tan(pi/8): atan(a) = pi/4 + atan((a-1)/(a+1))
    a = (a - 1) / (a + 1);
    base = PI / 4;
  }
  // halve once: atan(t) = 2*atan(t / (1 + sqrt(1 + t^2)))  -> |t| <= 0.199
  let r = base + 2 * atanSmall(a / (1 + Math.sqrt(1 + a * a)));
  if (invert) r = HALF_PI - r;
  return neg ? -r : r;
}
/** Deterministic atan2(y, x) (radians), same quadrant rules as Math.atan2 for finite inputs. */
export function datan2(y: number, x: number): number {
  if (x > 0) return datan(y / x);
  if (x < 0) return y >= 0 ? datan(y / x) + PI : datan(y / x) - PI;
  if (y > 0) return HALF_PI;
  if (y < 0) return -HALF_PI;
  return 0;
}
export function dasin(x: number): number {
  return datan2(x, Math.sqrt(Math.max(0, 1 - x * x)));
}
export function dacos(x: number): number {
  return datan2(Math.sqrt(Math.max(0, 1 - x * x)), x);
}
/** Degree variants. */
export function dsinDeg(deg: number): number { return dsin(deg * DEG2RAD); }
export function dcosDeg(deg: number): number { return dcos(deg * DEG2RAD); }

// ---------------------------------------------------------------------------------------------
// Angles
// ---------------------------------------------------------------------------------------------
/** Q3 ANGLE2SHORT: ((int)(x*65536/360)) & 65535  [NET §1.3]. Result 0..65535. */
export function angleToShort(deg: number): number {
  return ((deg * 65536) / 360 | 0) & 65535;
}
/** Q3 SHORT2ANGLE: x * 360/65536 (0.005493164 @0xbd3110 in t6mp) [PM §2]. */
export function shortToAngle(s: number): number {
  return s * (360 / 65536);
}
/** Quantize an angle through a short and back (client must predict with quantized angles, pitfall 5). */
export function quantizeAngle(deg: number): number {
  return shortToAngle(angleToShort(deg));
}
/** Signed 16-bit view of a short (for deltas / pitch). */
export function shortToSigned(s: number): number {
  return (s << 16) >> 16;
}
/** Q3 AngleMod / AngleNormalize360: [0, 360). */
export function angleNormalize360(deg: number): number {
  return (360 / 65536) * (((deg * (65536 / 360)) | 0) & 65535);
}
/** Q3 AngleNormalize180: (-180, 180]. */
export function angleNormalize180(deg: number): number {
  const a = angleNormalize360(deg);
  return a > 180 ? a - 360 : a;
}
/** Shortest signed difference a - b in (-180, 180]. */
export function angleSubtract(a: number, b: number): number {
  let d = a - b;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}
/** Shortest-arc lerp between two angles in degrees. */
export function lerpAngle(from: number, to: number, t: number): number {
  return from + angleSubtract(to, from) * t;
}

/**
 * Q3 AngleVectors with deterministic trig. angles = [pitch, yaw, roll] degrees (CoD space).
 * forward = (cp*cy, cp*sy, -sp). Any of the outputs may be null.
 */
export function angleVectors(angles: ReadonlyVec3, forward: Vec3 | null, right: Vec3 | null, up: Vec3 | null): void {
  const yr = angles[YAW] * DEG2RAD;
  const sy = dsin(yr), cy = dcos(yr);
  const pr = angles[PITCH] * DEG2RAD;
  const sp = dsin(pr), cp = dcos(pr);
  const rr = angles[ROLL] * DEG2RAD;
  const sr = dsin(rr), cr = dcos(rr);
  if (forward) {
    forward[0] = cp * cy; forward[1] = cp * sy; forward[2] = -sp;
  }
  if (right) {
    right[0] = -1 * sr * sp * cy + -1 * cr * -sy;
    right[1] = -1 * sr * sp * sy + -1 * cr * cy;
    right[2] = -1 * sr * cp;
  }
  if (up) {
    up[0] = cr * sp * cy + -sr * -sy;
    up[1] = cr * sp * sy + -sr * cy;
    up[2] = cr * cp;
  }
}
/** Q3 vectoangles (deterministic). Returns [pitch, yaw, 0] in degrees, yaw in [0,360). */
export function vectorToAngles(out: Vec3, v: ReadonlyVec3): Vec3 {
  let yaw: number, pitch: number;
  if (v[1] === 0 && v[0] === 0) {
    yaw = 0;
    pitch = v[2] > 0 ? 270 : 90;
  } else {
    if (v[0]) yaw = datan2(v[1], v[0]) * RAD2DEG;
    else yaw = v[1] > 0 ? 90 : 270;
    if (yaw < 0) yaw += 360;
    const forward = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
    pitch = datan2(v[2], forward) * RAD2DEG;
    if (pitch < 0) pitch += 360;
  }
  out[PITCH] = -pitch;
  out[YAW] = yaw;
  out[ROLL] = 0;
  return out;
}

// ---------------------------------------------------------------------------------------------
// Deterministic random (spread, bounce jitter) — identical on client prediction and server
// ---------------------------------------------------------------------------------------------
/** Integer hash -> float in [0, 1). Use seeds derived from networked state (e.g. ps.commandTime, ps.fireSeq). */
export function hashRandom(a: number, b = 0): number {
  let h = Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca77);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/** Small seedable PRNG (mulberry32) for server-only randomness (spawns, bots). */
export class Rng {
  private s: number;
  constructor(seed: number) { this.s = seed >>> 0; }
  next(): number {
    let t = (this.s = (this.s + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(lo: number, hi: number): number { return lo + (hi - lo) * this.next(); }
  int(n: number): number { return Math.floor(this.next() * n); }
}

// ---------------------------------------------------------------------------------------------
// Render boundary: CoD (Z-up) <-> three.js (Y-up). Plain arrays so shared/ stays three-free.
// ---------------------------------------------------------------------------------------------
/** three = (cod.x, cod.z, -cod.y) */
export function codToThree(out: Vec3, cod: ReadonlyVec3): Vec3 {
  const x = cod[0], y = cod[1], z = cod[2];
  out[0] = x; out[1] = z; out[2] = -y;
  return out;
}
/** cod = (three.x, -three.z, three.y) */
export function threeToCod(out: Vec3, t: ReadonlyVec3): Vec3 {
  const x = t[0], y = t[1], z = t[2];
  out[0] = x; out[1] = -z; out[2] = y;
  return out;
}
/**
 * CoD view angles -> three.js Euler (order 'YXZ', radians) for a camera / object whose
 * default forward is three -Z.  yaw 0 (cod +X) -> rotation.y = -90deg.  Positive cod pitch (down)
 * -> negative rotation.x.  Roll sign: ESTIMATE (cod roll right = clockwise) — verify visually.
 * Usage: camera.rotation.set(e[0], e[1], e[2], 'YXZ').
 */
export function codAnglesToThreeEuler(out: Vec3, angles: ReadonlyVec3): Vec3 {
  out[0] = -angles[PITCH] * DEG2RAD;
  out[1] = (angles[YAW] - 90) * DEG2RAD;
  out[2] = -angles[ROLL] * DEG2RAD;
  return out;
}
/** For objects modelled facing three +X (e.g. GLBs with a `cod_root` node): rotation.y = yaw. */
export function codYawToThreeYawForPlusX(yawDeg: number): number {
  return yawDeg * DEG2RAD;
}
